'use client';

import { IcArrowDown, Typography } from '@musinsa/mds';
import { useEffect, useRef, useState } from 'react';

interface DropDownMenuItem {
  label: string;
  value: string;
}

interface DropDownMenuProps {
  items: DropDownMenuItem[];
  selectedIndex: number;
  onSelect: (value: string, index: number) => void;
}

export const DropDownMenu = ({ items, selectedIndex, onSelect }: DropDownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // safari scroll 이슈 대응: 열렸을 때만 relative
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.position = isOpen ? 'relative' : 'static';
  }, [isOpen]);

  if (!items.length) return null;

  return (
    <div ref={containerRef} className="w-auto">
      <button
        type="button"
        className="flex items-center justify-between text-gray-600"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Typography variant="body13pxReg">{items[selectedIndex]?.label ?? ''}</Typography>
        {/* MDS 아이콘 색은 strokeColor prop 으로만 적용됨 (className=text-* 는 stroke 에 무효). #666666=gray-600. (SID-4456) */}
        <IcArrowDown strokeColor="gray-600" width={12} height={12} className="ml-[2px]" />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 z-20 box-border min-w-[120px] mt-1 py-1 bg-white border-gray-300 border rounded">
          {items.map((item, i) => (
            <button
              type="button"
              className="flex items-center justify-between w-full h-8 px-3 overflow-hidden whitespace-nowrap"
              key={item.value}
              onClick={() => {
                onSelect(item.value, i);
                setIsOpen(false);
              }}
            >
              <Typography
                variant="body13pxReg"
                colorScheme={i === selectedIndex ? 'black' : 'gray-500'}
              >
                {item.label}
              </Typography>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
